/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { ArrowRight, Info, type LucideIcon } from 'lucide-react';
import { NavLink } from 'react-router';
import { useState, type MouseEvent, type ReactNode } from 'react';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';
import { useOverviewDisplay } from '@/context/OverviewDisplayContext';

/* Import libraries */
import { getStatusClassName, type StatusSeverity } from '@/lib/overview/overviewCards';

export interface SystemDashboardTileData {
    actionIcon?: LucideIcon;
    detail?: string;
    detailIsSensor?: boolean;
    modal?: {
        content: ReactNode;
        title: string;
    };
    onClick?: () => void;
    severity: StatusSeverity;
    statusLabel?: string;
    title: string;
    to?: string;
    unit?: string | null;
    value: string;
}

function SystemDashboardTile({
    actionIcon: ActionIcon,
    detail,
    detailIsSensor = false,
    modal,
    onClick,
    severity,
    statusLabel,
    title,
    to,
    unit,
    value,
}: SystemDashboardTileData) {
    const { t } = useI18n();
    const { showKpis } = useOverviewDisplay();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const isActionTile = ActionIcon !== undefined || modal !== undefined;
    const isInteractive = to !== undefined || onClick !== undefined;
    const tileTitle = isActionTile || showKpis ? detail : undefined;
    const isStatusKpi = statusLabel !== undefined && value !== '';
    const className = `dashboard-panel h-full min-h-28 2xl:min-h-0 items-stretch justify-stretch p-0 ${getStatusClassName(severity)}${isInteractive ? ' system-dashboard__tile-link interactive-surface' : ''}`;
    const openModal = (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsModalOpen(true);
    };
    const content = (
        <div className="relative flex min-w-0 flex-1 flex-col justify-center px-6 py-3">
            <div className={`flex min-w-0 flex-col items-center justify-center gap-2 text-center${isInteractive ? ' pr-14' : ''}`}>
                <div className="flex min-w-0 items-center justify-center gap-1.5">
                    <h3 className="mb-0 truncate text-base font-semibold text-base-content">
                        {title}
                    </h3>
                    {modal ? (
                        <button
                            type="button"
                            className="btn btn-ghost btn-xs btn-circle shrink-0 text-base-content/55 hover:text-base-content"
                            aria-label={modal.title}
                            onClick={openModal}
                        >
                            <Info className="h-4 w-4" aria-hidden="true" />
                        </button>
                    ) : ActionIcon ? (
                        <ActionIcon className="h-5 w-5 shrink-0 text-base-content/55" aria-hidden="true" />
                    ) : null}
                </div>
                {isStatusKpi ? (
                    <>
                        <div className="inline-flex max-w-full items-baseline justify-center gap-1.5 tabular-nums text-base-content">
                            <span className="truncate text-3xl leading-tight font-bold tracking-tight">{value}</span>
                            {unit ? <span className="shrink-0 text-base font-semibold text-base-content/70">{unit}</span> : null}
                        </div>
                        <p className="mb-0 truncate text-sm font-semibold text-base-content/80">
                            {t('Status')}: {statusLabel}
                        </p>
                    </>
                ) : (
                    <p className="mb-0 text-center text-base font-semibold text-base-content">
                        {value}
                    </p>
                )}
            </div>
            {isInteractive ? (
                <span className="absolute top-1/2 right-6 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-2 border-base-content/50 text-base-content/70">
                    <ArrowRight className="h-5 w-5" aria-hidden="true" />
                </span>
            ) : null}
            {showKpis && detail ? (
                <p className={`absolute right-6 bottom-3 left-6 mb-0 truncate text-left text-xs text-base-content/55${detailIsSensor ? ' font-mono' : ''}`}>
                    {detail}
                </p>
            ) : null}
        </div>
    );

    const modalDialog = isModalOpen && modal ? (
        <div
            className="modal modal-open"
            role="dialog"
            aria-modal="true"
            aria-labelledby="system-dashboard-tile-modal-title"
        >
            <div className="modal-box flex max-h-[calc(100vh-2rem)] max-w-4xl flex-col">
                <div className="mb-4 flex items-start justify-between gap-4">
                    <h2 id="system-dashboard-tile-modal-title">{modal.title}</h2>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-circle"
                        aria-label={t('Close')}
                        onClick={() => setIsModalOpen(false)}
                    >
                        ✕
                    </button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto pr-1">{modal.content}</div>

                <div className="modal-action">
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setIsModalOpen(false)}
                    >
                        {t('OK')}
                    </button>
                </div>
            </div>
            <div className="modal-backdrop bg-black/40" onClick={() => setIsModalOpen(false)} />
        </div>
    ) : null;

    if (to) {
        return (
            <>
                <NavLink className={className} to={to} title={tileTitle}>
                    {content}
                </NavLink>
                {modalDialog}
            </>
        );
    }

    if (onClick) {
        return (
            <>
                <a
                    className={className}
                    href="#coming-soon"
                    onClick={(event) => {
                        event.preventDefault();
                        onClick();
                    }}
                    title={tileTitle}
                >
                    {content}
                </a>
                {modalDialog}
            </>
        );
    }

    return (
        <>
            <article className={className} title={tileTitle}>
                {content}
            </article>
            {modalDialog}
        </>
    );
}

export default SystemDashboardTile;
